import { useQuery } from '@tanstack/react-query';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { FormDefinition } from 'features/entry-form-v2/data/useGetFormsQuery/useGetFormsQuery';
import { Dependency } from 'features/entry-form-v2/data/useGetVariableDependenciesQuery/useGetVariableDependenciesQuery';
import { toProjectData } from 'features/entry-form-v2/data/useProjectData/useProjectData';
import { VariablesData } from 'features/entry-form-v2/data/useVariablesQuery/useVariablesQuery';
import { useMemo } from 'react';

export type SurveyForm = {
	variables: VariablesData;
	dependencies: Dependency[];
	form: FormDefinition;
};

export const useGetSurveyFormQuery = ({
	surveyId,
	pinCode
}: {
	surveyId: string;
	pinCode: string;
}) => {
	return useQuery({
		queryKey: ['survey', surveyId, pinCode],
		queryFn: async () => {
			const result = await makeRequest<SurveyForm>({
				method: 'getSurveyForm',
				service: 'cde',
				validateCredentials: false,
				data: {
					surveyId: surveyId,
					pinCode: pinCode
				}
			});

			return result;
		},
		enabled: !!pinCode,
		retry: (failureCount, error) => {
			// Don't retry on specific survey errors
			if (error instanceof Error && 'errors' in error) {
				const surveyError = error as any;
				const errorCodes = surveyError.errors?.map((e: any) => e.code) || [];
				const shouldNotRetry = [
					'error.wrongPinCode',
					'error.expiredPinCode',
					'error.surveyAlreadyCompleted',
					'error.maxAttemptsExceeded'
				];

				if (errorCodes.some((code: string) => shouldNotRetry.includes(code))) {
					return false;
				}
			}

			const shouldRetry = failureCount < 3;
			return shouldRetry;
		}
	});
};

export const useSurveyForm = ({ surveyId, pinCode }: { surveyId: string; pinCode?: string }) => {
	const getSurveyFormQuery = useGetSurveyFormQuery({ surveyId, pinCode: pinCode || '' });

	const processedData = useMemo(() => {
		if (getSurveyFormQuery.data) {
			const result = toProjectData({
				variablesData: getSurveyFormQuery.data.variables,
				dependenciesData: {
					active: true,
					dependencies: getSurveyFormQuery.data.dependencies
				},
				activeForm: {
					formDefinition: getSurveyFormQuery.data.form,
					is_active: true,
					formId: 1
				}
			});

			return result;
		}
	}, [getSurveyFormQuery]);

	return {
		data: processedData,
		isLoading: getSurveyFormQuery.isLoading,
		isFetching: getSurveyFormQuery.isFetching,
		error: getSurveyFormQuery.error,
		refetch: getSurveyFormQuery.refetch,
		isSuccess: getSurveyFormQuery.isSuccess
	};
};
